/** @format */

import { Component } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Company } from "src/app/models/company.model";
import { DataPoint } from "src/app/models/datapoint.model";
import { CompanyService } from "src/app/services/company/company.service";
import { DataPointService } from "src/app/services/data-point/data-point.service";
import { ErrorHandlerService } from "src/app/services/errorHandler/error-handler.service";
import { PlantService } from "src/app/services/plant/plant.service";
import { ToastService } from "src/app/services/toast/toast.service";
import { dataPointTypes } from "src/app/models/data-point-types";
import { Roles } from "src/app/auth/roles";

@Component({
  standalone: false,
  selector: "app-add-data-point",
  templateUrl: "./add-data-point.component.html",
  styleUrls: ["./add-data-point.component.css"],
})
export class AddDataPointComponent {
	readonly Roles = Roles;
  values: string[] = [];
  selectedValues = {
    company: { companyId: 0, companyName: "" },
    plant: { plantId: 0, plantName: "" },
    unit: { unitId: 0, unitName: "" },
  };

  selectedFile: File | null = null;
  uploadProgress: number = 0;
  uploadError: boolean = false;
  csvUploaded: boolean = false; // Tracks if a CSV has been uploaded successfully

  fractionMessage =
    "Use a forward slash to separate numerator from denominator. For example (1/2 or 7/4)";
  listMessage =
    "Use a comma to separate each List Form Field Type option. For example (None,Amber,Red,Green)";
  validationErrorMessage = "";

  dataPointTypes = [
    { id: 1, pointCategory: "Single Point Name", isDeleted: "none" },
    { id: 2, pointCategory: "Smart Form Field", isDeleted: false },
  ];

  // Sourced from the shared data-point types (single source of truth shared
  // with the workflow builder) rather than a local hard-coded list.
  dataTypes = dataPointTypes();

  selectedDataType = { id: 0, dataPointType: "", isDeleted: false };
  selectedDataPointCategory = { id: 0, pointCategory: "", isDeleted: false };

  optionsList: string[] = [""];
  // Date Range label config (persisted into listOptions as "start,end").
  dateRangeStartLabel: string = "Start Date";
  dateRangeEndLabel: string = "End Date";
  // Reference Number config (persisted into listOptions as "prefix,padding").
  refnumPrefix: string = "REF-";
  refnumPadding: number = 4;
  // File Upload config (persisted into listOptions as "types,maxFiles,maxSizeMB").
  fileTypes: string = "any";
  fileMaxFiles: number = 1;
  fileMaxSizeMB: number = 10;
  // Duration config: a self-contained field with its own start/end inputs.
  // Only the unit is configured here and persisted into listOptions.
  durationUnit: string = "days";
  highlightEmptyOption: boolean = false;
  private highlightTimer: any;

  addDataPointRequest: {
    dataPointId: number;
    dataPointName: string;
    dataType: string;
    unitOfMeasurement: string;
    listOptions: string;
    fraction: string;
    dataPointMin: number;
    dataPointMax: number;
    dataPointDescription: string;
    isDeleted: boolean;
    status: string;
    unitId: number;
    smartDataPoints: string[];
  } = {
      dataPointId: 0,
      dataPointName: "Default",
      dataType: "",
      unitOfMeasurement: "",
      listOptions: "",
      fraction: "",
      dataPointMin: 0,
      dataPointMax: 0,
      dataPointDescription: "",
      isDeleted: false,
      status: "active",
      unitId: 0,
      smartDataPoints: [],
    };

  errorMessage: any = { success: true, message: "" };

  companies: Company[] = [];
  plants: any;
  retrievedCompany: any;
  currentRole: any = "";
  firstRender: boolean = false;
  displayOverlay: boolean = false;
  unitInput: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dataPointService: DataPointService,
    private companyService: CompanyService,
    public toastService: ToastService,
    private errorService: ErrorHandlerService,
  ) { }

  ngOnInit(): void {
    this.selectedValues = history.state;
    this.currentRole = localStorage.getItem("role");
    this.dataTypes.sort((a, b) =>
      a.dataPointType.localeCompare(b.dataPointType),
    );
  }

  transferData() {
    this.firstRender = true;
    this.router.navigateByUrl("settings/form-fields", {
      state: {
        selectedValues: { ...this.selectedValues },
        firstRender: this.firstRender,
      },
    });
  }

  toggleOverlay() {
    this.displayOverlay = !this.displayOverlay;
  }

  syncStringFromOptionsList() {
    this.addDataPointRequest.listOptions = this.optionsList
      .map((v) => v.trim())
      .filter((v) => v !== "")
      .join(",");
  }

  canAddOption(): boolean {
    return this.optionsList.length === 0 || this.optionsList[this.optionsList.length - 1].trim() !== '';
  }

  addOption() {
    if (!this.canAddOption()) {
      this.showEmptyHighlight();
      return;
    }
    this.optionsList.push("");
  }

  showEmptyHighlight() {
    this.highlightEmptyOption = true;
    clearTimeout(this.highlightTimer);
    this.highlightTimer = setTimeout(() => {
      this.highlightEmptyOption = false;
    }, 5000);
  }

  removeOption(index: number) {
    if (this.optionsList.length <= 1) return;
    this.optionsList.splice(index, 1);
    this.syncStringFromOptionsList();
  }

  onOptionChange() {
    this.syncStringFromOptionsList();
  }

  trackByIndex(index: number): number {
    return index;
  }

  activateSubmitBtn() {
    const isListType =
      this.selectedDataType.dataPointType === "List (Multiple Selection)" ||
      this.selectedDataType.dataPointType === "List (Single Selection)" ||
      this.selectedDataType.dataPointType === "Dropdown List";

    if (isListType) {
      const hasManualList =
        this.addDataPointRequest.listOptions
          .split(",")
          .filter((v) => v.trim() !== "").length >= 1;

      const hasCsvUpload =
        this.addDataPointRequest.smartDataPoints &&
        this.addDataPointRequest.smartDataPoints.length >= 3;

      if (!hasManualList && !hasCsvUpload) {
        return false;
      }
    }

    if (
      this.selectedDataType.id === 0 ||
      this.addDataPointRequest.dataPointName.length < 3
    ) {
      return false;
    }

    if (
      this.selectedDataType.dataPointType === "Number" &&
      (this.addDataPointRequest.unitOfMeasurement.length === 0 ||
        this.addDataPointRequest.dataPointMax == null ||
        this.addDataPointRequest.dataPointMin == null ||
        this.addDataPointRequest.dataPointMin >=
        this.addDataPointRequest.dataPointMax)
    ) {
      return false;
    }

    return true;
  }

  setSelectedDataPointType(selected: any) {
    this.selectedDataType = selected;
  }

  setSelectedDataPointCategory(selected: any) {
    this.selectedDataPointCategory = selected;
  }

  /**
   * CSV File Handling with Progress Animation
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    // Reset upload state
    this.selectedFile = null;
    this.uploadProgress = 0;
    this.uploadError = false;
    this.validationErrorMessage = "";
    this.addDataPointRequest.smartDataPoints = [];

    // Validate CSV extension
    if (!file.name.toLowerCase().endsWith(".csv")) {
      this.validationErrorMessage = "Only CSV files are allowed.";
      this.uploadError = true;
      return;
    }

    this.selectedFile = file;

    const reader = new FileReader();

    // Animate progress visually
    let progressValue = 0;
    const progressInterval = setInterval(() => {
      progressValue += Math.random() * 10;
      if (progressValue >= 95) progressValue = 95;
      this.uploadProgress = Math.round(progressValue);
    }, 200);

    reader.onload = () => {
      clearInterval(progressInterval);
      const text = reader.result as string;
      const values = this.parseCsvToList(text);

      if (values.length < 3) {
        this.validationErrorMessage =
          "CSV must have at least 3 values in a column.";
        this.addDataPointRequest.listOptions = "";
        this.uploadError = true;
        this.uploadProgress = 100;
        this.csvUploaded = false;
        return;
      }

      // **Combine CSV values into a single Form Field's listOptions**
      this.addDataPointRequest.listOptions = values.join(",");
      this.uploadProgress = 100;
      this.uploadError = false;
      this.csvUploaded = true;
    };

    reader.onerror = () => {
      clearInterval(progressInterval);
      this.validationErrorMessage = "Error reading CSV file.";
      this.uploadError = true;
      this.uploadProgress = 100;
    };

    reader.readAsText(file);
  }

  private parseCsvToList(csvText: string): string[] {
    const lines = csvText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    // Take only first column
    const values = lines.map((line) => line.split(",")[0].trim());
    return values.filter((v) => v.length > 0);
  }

  downloadSampleCsv() {
    const sampleData = ["Option1", "Option2", "Option3", "etc"];
    const csvContent = sampleData.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "sample-data.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Known File Upload categories the mobile picker maps straight to a native
  // filter (FileType.image/.video/.audio/.media) instead of treating the
  // word itself as a file extension.
  private static readonly FILE_UPLOAD_CATEGORIES = ["image", "video", "audio", "media"];

  // Normalizes the "Allowed File Types" input into what mobile's
  // parseFileUploadConfig expects: "any", a known category, or bare
  // extensions joined with ";" (not ",", which is the outer listOptions
  // delimiter for types/maxFiles/maxSizeMB).
  sanitizeFileTypes(raw: string): string {
    const trimmed = (raw || "").trim().toLowerCase();
    if (trimmed === "" || trimmed === "any") {
      return "any";
    }
    if (AddDataPointComponent.FILE_UPLOAD_CATEGORIES.includes(trimmed)) {
      return trimmed;
    }
    return trimmed
      .split(",")
      .map((ext) => ext.trim().replace(/^\./, ""))
      .filter((ext) => ext !== "")
      .join(";");
  }

  addDataPoint() {
    this.addDataPointRequest.dataType = this.selectedDataType.dataPointType;
    this.addDataPointRequest.unitId = this.selectedValues["unit"].unitId;

    if (this.addDataPointRequest.dataType === "Date Range") {
      const start = (this.dateRangeStartLabel || "Start Date").trim();
      const end = (this.dateRangeEndLabel || "End Date").trim();
      this.addDataPointRequest.listOptions = `${start},${end}`;
    }

    if (this.addDataPointRequest.dataType === "Reference Number") {
      const prefix = (this.refnumPrefix || "REF-").trim();
      const padding = this.refnumPadding > 0 ? this.refnumPadding : 4;
      this.addDataPointRequest.listOptions = `${prefix},${padding}`;
    }

    if (this.addDataPointRequest.dataType === "File Upload") {
      const types = this.sanitizeFileTypes(this.fileTypes);
      const maxFiles = this.fileMaxFiles > 0 ? this.fileMaxFiles : 1;
      const maxSize = this.fileMaxSizeMB > 0 ? this.fileMaxSizeMB : 10;
      this.addDataPointRequest.listOptions = `${types},${maxFiles},${maxSize}`;
    }

    if (this.addDataPointRequest.dataType === "Duration") {
      // Self-contained: only the unit is configured; start/end are entered on the form.
      this.addDataPointRequest.listOptions = this.durationUnit || "days";
    }

    const isListType =
      this.addDataPointRequest.dataType === "List" ||
      this.addDataPointRequest.dataType === "List (Multiple Selection)" ||
      this.addDataPointRequest.dataType === "List (Single Selection)" ||
      this.addDataPointRequest.dataType === "Dropdown List";

    const hasCsvUpload =
      this.addDataPointRequest.smartDataPoints &&
      this.addDataPointRequest.smartDataPoints.length >= 3;

    if (isListType && !hasCsvUpload) {
      const listOptions = this.addDataPointRequest.listOptions;
      const items = listOptions.split(",").map(part => part.trim()).filter(part => part !== "");

      if (items.length < 2) {
        this.validationErrorMessage =
          "Please enter at least two items for the list";
        return;
      } else {
        this.validationErrorMessage = "";
      }
    }

    if (this.addDataPointRequest.dataType !== "Number") {
      this.addDataPointRequest.unitOfMeasurement = "";
      this.addDataPointRequest.dataPointMin = 0;
      this.addDataPointRequest.dataPointMax = 0;
    }

    this.dataPointService.addDataPoint(this.addDataPointRequest).subscribe({
      next: () => {
        this.transferData();
        this.toastService.showToast = true;
        this.toastService.toastText = "Form Field successfully added!";
        setTimeout(() => {
          this.toastService.showToast = false;
        }, 5000);
      },
      error: (error) => {
        this.errorService.handleError(error, "Error adding form field");
      },
    });
  }
}
